import React, { useState } from "react";
import EditSheet from "./EditSheet";

const TAG_COLORS = {
  Email: { bg: "#F1F1EF", text: "#787774" },
  Call: { bg: "#DDEDEA", text: "#2B8A3E" },
  Pay: { bg: "#EEE0DA", text: "#8C5B3F" },
  Schedule: { bg: "#EAE4F2", text: "#6940A5" },
  "Sign Up": { bg: "#FBF3DB", text: "#9F6B00" },
};

function TagPills({ tags }) {
  if (!tags || tags.length === 0) return null;
  return tags.map(t => {
    const c = TAG_COLORS[t] || { bg: "var(--cream-hover)", text: "var(--muted)" };
    return (
      <React.Fragment key={t}>
        <span className="meta-dot" />
        <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20, background: c.bg, color: c.text }}>{t}</span>
      </React.Fragment>
    );
  });
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  const date = new Date(dateStr + "T00:00:00");
  const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: dateStr, soon: true };
  if (diffDays === 0) return { label: "Due today", soon: true };
  if (diffDays === 1) return { label: "Due tomorrow", soon: true };
  if (diffDays <= 7) return { label: `Due ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`, soon: true };
  return { label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }), soon: false };
}

async function updateTask(pageId, action, value) {
  await fetch("/.netlify/functions/notion", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pageId, action, value }),
  });
}

function SkeletonRow() {
  return (
    <div className="skeleton-row">
      <div className="skeleton skeleton-circle" />
      <div className="skeleton-lines">
        <div className="skeleton skeleton-line-long" />
        <div className="skeleton skeleton-line-short" />
      </div>
      <div className="skeleton" style={{ width: 20, height: 20, borderRadius: 4 }} />
    </div>
  );
}

export default function TasksSection({ items: initialItems, loading, onSeeAll }) {
  const [items, setItems] = useState(initialItems);
  const [editItem, setEditItem] = useState(null);

  React.useEffect(() => { setItems(initialItems); }, [initialItems]);

  const handleCheck = async (e, id) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((i) => i.id !== id));
    await updateTask(id, "toggleDone", true);
  };

  const handleStar = async (e, id, current) => {
    e.stopPropagation();
    const newVal = !current;
    setItems((prev) => prev.map((i) => i.id === id ? { ...i, isHighPriority: newVal } : i));
    await updateTask(id, "togglePriority", newVal);
  };

  const handleSave = (updated) => {
    setItems((prev) => prev.map((i) => i.id === updated.id ? updated : i));
    setEditItem(null);
  };

  const handleDelete = (id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    setEditItem(null);
  };

  return (
    <>
      <div>
        <div className="section-head">
          <span className="section-label">Tasks</span>
        </div>
        <div className="item-list">
          {loading ? (
            <><SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🎉</div>
              <div className="empty-text">All caught up!</div>
            </div>
          ) : (
            items.map((item) => {
              const d = formatDate(item.dueDate);
              const showTypePill = item.type && item.type !== "To Do";
              return (
                <div key={item.id} className="task-row" onClick={() => setEditItem(item)}>
                  <button className="task-check" onClick={(e) => handleCheck(e, item.id)} aria-label="Complete task" />
                  <div className="task-content">
                    <div className="task-name">{item.name}</div>
                    <div className="task-sub">
                      {d ? (
                        <span className={`task-date${d.soon ? " soon" : ""}`}>{d.label}</span>
                      ) : (
                        <span className="task-date">No due date</span>
                      )}
                      {showTypePill && (<><span className="meta-dot" /><span className="type-pill">{item.type}</span></>)}
                      <TagPills tags={item.tag} />
                    </div>
                  </div>
                  <button
                    className={`task-star${item.isHighPriority ? " starred" : ""}`}
                    onClick={(e) => handleStar(e, item.id, item.isHighPriority)}
                    aria-label="Toggle priority"
                  >{item.isHighPriority ? "★" : "☆"}</button>
                </div>
              );
            })
          )}
        </div>
        <div onClick={onSeeAll} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", background: "var(--cream-hover)", borderTop: "1px solid var(--border)", cursor: "pointer" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--sage)" }}>See all tasks</span>
          <span style={{ fontSize: 16, color: "var(--sage)" }}>›</span>
        </div>
      </div>
      <EditSheet item={editItem} onClose={() => setEditItem(null)} onSave={handleSave} onDelete={handleDelete} />
    </>
  );
}
